import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { fetchMovies } from '../api/client';
import DataTable from '../components/DataTable';
import AnchoredMenu from '../components/AnchoredMenu';
import MovieActionPanel from '../components/MovieActionPanel';
import { israelToday } from '../components/DateRangePicker';
import Poster from '../components/Poster';
import { occColor } from '../components/Podium';
import { ageText } from '../components/ageLabel';
import { TEXT_SECONDARY, TEXT_TERTIARY, TEXT_FAINT, ACCENT } from '../theme';
import { useAuth } from '../context/AuthContext';

/**
 * The film archive (§12.12): every film in the system, A to Z. By default the
 * films that still have a screening ahead, whenever it is; a toggle adds the
 * rest. No date range and no sort — the figures are each film's whole run.
 * Admins have two more: only the films screening in the next 7 days, and the
 * films filtered from statistics, which nobody else ever sees here.
 */

const formatNumber = (val) => (val || 0).toLocaleString();
const nameOf = (m) => m.title_he || m.title || '';
const byName = (a, b) => nameOf(a).localeCompare(nameOf(b), 'he', { numeric: true, sensitivity: 'base' });

// '2026-10-12' → '12.10.2026', read left to right inside Hebrew.
const fmtDate = (s) => {
  if (!s) return '';
  const [y, m, d] = s.split('-').map(Number);
  return `${d}.${m}.${y}`;
};
const DateText = ({ value, today }) => (!value ? <span style={{ color: TEXT_FAINT }}>—</span> : (
  <span dir="ltr" style={{ unicodeBidi: 'isolate', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
                           color: value > today ? 'var(--presale)' : 'inherit', fontWeight: value > today ? 700 : 400 }}>
    {fmtDate(value)}
  </span>
));

const AgeChip = ({ label }) => {
  const text = ageText(label);
  if (!text) return null;
  return (
    <span style={{ background: 'rgba(239,68,68,0.1)', color: 'var(--danger)', padding: '1px 7px', borderRadius: 5,
                   fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap' }}>{text}</span>
  );
};

const Occ = ({ value }) => (
  <span style={{ color: occColor(value || 0), fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{Math.round(value || 0)}%</span>
);

const PAGE_SIZE = 50;

// Same pager button styling as components/RankingTable.js
const pagerBtn = (disabled) => ({
  padding: '5px 12px', borderRadius: 8, border: '1px solid var(--brd)',
  background: 'var(--surf)', color: disabled ? 'var(--faint)' : 'var(--txt)',
  cursor: disabled ? 'default' : 'pointer', fontFamily: 'Heebo, sans-serif', fontSize: 12,
  opacity: disabled ? 0.5 : 1,
});

// ─── Main Page ───────────────────────────────────────────────────────────────

function MoviesPage({ onMovieClick, refreshNonce }) {
  const { hasPermission, user } = useAuth();
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState('');
  // What the list covers: films with a screening ahead (default), every film
  // ('all' — "הצג גם סרטים ללא הקרנות"), or only those screening in the next
  // 7 days ('week', admins). One state, so the two buttons cannot both be on.
  const [scope, setScope] = useState('upcoming');
  const [showFiltered, setShowFiltered] = useState(false); // admins only
  const [page, setPage] = useState(0);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [width, setWidth] = useState(window.innerWidth);
  const menuRef = useRef(null);   // the ⋮ button's cell
  const panelRef = useRef(null);  // the panel itself, which lives in a portal
  const btnRef = useRef(null);    // what the panel is positioned against

  useEffect(() => {
    const fn = () => setWidth(window.innerWidth);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  const mob = width < 768;

  const loadMovies = useCallback(() => {
    return fetchMovies().then(data => {
      setMovies(Array.isArray(data) ? data : []);
      setLoadError(false);
      setLoading(false);
    }).catch(() => {
      setMovies([]);
      setLoadError(true);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    loadMovies();
  }, [loadMovies, refreshNonce]);

  // Close menu on outside click
  useEffect(() => {
    const handler = (e) => {
      const inCell = menuRef.current && menuRef.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inCell && !inPanel) setOpenMenuId(id => (id === null ? id : null));
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const hasAnyMovieAction = user?.is_admin || hasPermission('movies.approve_blocked') || hasPermission('movies.canonical_link') || hasPermission('movies.tmdb_match') || hasPermission('movies.filter_movie');
  const isAdmin = !!user?.is_admin;
  // A film filtered from statistics is shown to no one but an admin who asks.
  const withFiltered = isAdmin && showFiltered;

  // Search runs over the whole archive; only rendering is paginated. `hidden`
  // counts the matches the scope leaves out, so an empty search can offer them.
  const { filtered, hidden } = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matches = movies.filter(m => {
      if (m.is_filtered && !withFiltered) return false;
      if (!q) return true;
      return (
        (m.title || '').toLowerCase().includes(q) ||
        (m.title_he && m.title_he.toLowerCase().includes(q)) ||
        (m.title_en && m.title_en.toLowerCase().includes(q))
      );
    });
    const shown = scope === 'all' ? matches
      : matches.filter(m => ((scope === 'week' ? m.week_screenings : m.upcoming_screenings) || 0) > 0);
    return { filtered: [...shown].sort(byName), hidden: matches.length - shown.length };
  }, [movies, search, scope, withFiltered]);
  const today = israelToday();

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = useMemo(
    () => filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE),
    [filtered, safePage],
  );

  // Any change to what's being listed sends us back to the first page.
  // `movies` is deliberately absent: it gets a new identity on every refresh,
  // including the one a row action triggers, which reset the page under the user.
  useEffect(() => { setPage(0); }, [search, scope, withFiltered]);

  // The ⋮ menu of one film — the same on a desktop row and a phone card.
  const menuButton = (row) => (
    <div ref={openMenuId === row.id ? menuRef : null} style={{ flexShrink: 0, alignSelf: 'flex-start' }}>
      <button
        ref={openMenuId === row.id ? btnRef : null}
        onClick={(e) => {
          e.stopPropagation();
          setOpenMenuId(id => id === row.id ? null : row.id);
        }}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: TEXT_SECONDARY, fontSize: 18, padding: '0 6px', lineHeight: 1,
          fontFamily: 'monospace',
        }}
        title="פעולות"
      >⋮</button>
      {openMenuId === row.id && (
        <AnchoredMenu anchorRef={btnRef} panelRef={panelRef}>
          <MovieActionPanel
            movie={row}
            allMovies={movies}
            onClose={() => setOpenMenuId(null)}
            onRefresh={loadMovies}
          />
        </AnchoredMenu>
      )}
    </div>
  );

  const columns = [
    {
      header: '',
      render: (row) => (
        <div onClick={e => e.stopPropagation()}>
          <Poster posterUrl={row.poster_url} genre={row.genre} letter={nameOf(row)[0] || '?'} size="sm" />
        </div>
      ),
      align: 'center',
    },
    {
      header: 'סרט',
      render: (row) => (
        <div style={{ opacity: row.is_filtered ? 0.45 : 1, whiteSpace: 'normal', minWidth: 160 }}>
          <div style={{ fontWeight: 700 }}>
            {row.is_filtered && <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--danger)', marginLeft: 6, verticalAlign: 'middle' }}>מסונן</span>}
            {nameOf(row)}
          </div>
          {row.title_en && <div style={{ color: TEXT_FAINT, fontSize: 11, marginTop: 1 }}>{row.title_en}</div>}
        </div>
      ),
    },
    { header: 'ז\'אנר', render: (row) => <span style={{ color: TEXT_SECONDARY }}>{row.genre || '—'}</span> },
    { header: 'מפיץ', render: (row) => (row.distributor
        ? <span data-distributor style={{ color: TEXT_SECONDARY }}>{row.distributor}</span>
        : <span style={{ color: TEXT_FAINT }}>—</span>) },
    { header: 'גיל', render: (row) => (row.age_label ? <AgeChip label={row.age_label} /> : <span style={{ color: TEXT_FAINT }}>—</span>), align: 'center' },
    { header: 'הקרנה ראשונה', render: (row) => <DateText value={row.first_screening_date} today={today} />, align: 'center' },
    { header: 'הקרנות', render: (row) => formatNumber(row.screenings_count), align: 'center' },
    {
      header: 'כרטיסים',
      render: (row) => (
        <span style={{ color: ACCENT, fontWeight: 700, fontSize: 15 }}>
          {formatNumber(row.total_tickets_sold)}
        </span>
      ),
      align: 'center',
    },
    ...(hasPermission('movies.occupancy_bar') ? [{
      header: 'תפוסה',
      render: (row) => (row.screenings_count ? <Occ value={row.avg_occupancy} /> : <span style={{ color: TEXT_FAINT }}>—</span>),
      align: 'center',
    }] : []),
    ...(hasAnyMovieAction ? [{
      header: '',
      render: (row) => menuButton(row),
      align: 'center',
    }] : []),
  ];

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 60, color: TEXT_TERTIARY }}>טוען סרטים...</div>;
  }

  if (loadError) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <div style={{ color: 'var(--danger)', fontWeight: 700, marginBottom: 6 }}>שגיאה בטעינת רשימת הסרטים</div>
        <div style={{ color: TEXT_TERTIARY, fontSize: 13, marginBottom: 14 }}>
          ייתכן שהבקשה ארכה זמן רב מדי.
        </div>
        <button
          onClick={() => { setLoading(true); setLoadError(false); loadMovies(); }}
          style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: ACCENT, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'Heebo, sans-serif' }}
        >
          נסה שוב
        </button>
      </div>
    );
  }

  // A button that is on or off, in the style every list control here shares.
  const toggleBtn = (label, on, onClick, data) => (
    <button
      {...{ [`data-${data}`]: true }}
      onClick={onClick}
      style={{
        padding: '8px 14px', borderRadius: 8, fontSize: 13,
        fontWeight: on ? 700 : 400, fontFamily: 'Heebo, sans-serif',
        cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all .15s',
        border: `1px solid ${on ? 'var(--accent)' : 'var(--brd)'}`,
        background: on ? 'var(--accent)' : 'var(--surf-alt)',
        color: on ? '#fff' : 'var(--muted)',
      }}
    >
      {label}
    </button>
  );
  const flip = (to) => setScope(s => (s === to ? 'upcoming' : to));

  const controls = (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', width: mob ? '100%' : 'auto' }}>
      <input
        data-movie-search
        type="text"
        placeholder="חיפוש סרט..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{
          background: 'var(--surf-alt)', border: '1px solid var(--brd)',
          borderRadius: 8, padding: '8px 12px', color: 'var(--txt)',
          fontSize: 14, fontFamily: 'Heebo, sans-serif', outline: 'none',
          flex: mob ? '1 1 140px' : 'none', width: mob ? 'auto' : 220, minWidth: 0,
        }}
      />
      {toggleBtn('הצג גם סרטים ללא הקרנות', scope === 'all', () => flip('all'), 'include-empty')}
      {isAdmin && toggleBtn('רק השבוע הקרוב', scope === 'week', () => flip('week'), 'week-only')}
      {isAdmin && toggleBtn('הצג סרטים מסוננים', showFiltered, () => setShowFiltered(v => !v), 'show-filtered')}
    </div>
  );

  // Nothing matched within the scope, but the rest of the archive has matches:
  // one tap shows everything instead of a dead end.
  const empty = filtered.length === 0 && (
    <div data-movies-empty style={{ textAlign: 'center', padding: '40px 16px', color: TEXT_TERTIARY, fontSize: 14 }}>
      <div style={{ marginBottom: hidden ? 12 : 0 }}>לא נמצאו סרטים</div>
      {hidden > 0 && scope !== 'all' && (
        <button data-show-hidden onClick={() => setScope('all')} style={{
          padding: '8px 16px', borderRadius: 8, border: '1px solid var(--accent)', background: 'transparent',
          color: 'var(--accent)', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'Heebo, sans-serif',
        }}>{scope === 'week'
          ? `עוד ${hidden === 1 ? 'סרט אחד' : `${hidden} סרטים`}`
          : `${hidden === 1 ? 'סרט אחד' : `${hidden} סרטים`} ללא הקרנות`}</button>
      )}
    </div>
  );

  // Matches the pager in components/RankingTable.js (chevrons included, so the
  // direction reads correctly in RTL like everywhere else in the app).
  const pager = pageCount > 1 && (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14,
      padding: '12px 0', fontSize: 12, color: 'var(--muted)',
    }}>
      <button
        onClick={() => setPage(Math.max(0, safePage - 1))}
        disabled={safePage === 0}
        style={pagerBtn(safePage === 0)}
      >‹ הקודם</button>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>
        {safePage + 1} / {pageCount} · {filtered.length} סרטים
      </span>
      <button
        onClick={() => setPage(Math.min(pageCount - 1, safePage + 1))}
        disabled={safePage >= pageCount - 1}
        style={pagerBtn(safePage >= pageCount - 1)}
      >הבא ›</button>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: mob ? 'flex-start' : 'center', flexDirection: mob ? 'column' : 'row', gap: 10, marginBottom: 12 }}>
        {!mob && <h2 style={{ fontSize: 22, fontWeight: 700 }}>סרטים</h2>}
        {hasPermission('movies.search_filter') && controls}
      </div>

      {empty || (mob ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {pageRows.map(mv => (
            <div key={mv.id} data-movie-card
              style={{ background: 'var(--surf)', borderRadius: 16, boxShadow: 'var(--shadow)', padding: 12, cursor: hasPermission('movie_detail.access') ? 'pointer' : 'default', display: 'flex', gap: 12, alignItems: 'center', opacity: mv.is_filtered ? 0.5 : 1 }}
              onClick={() => hasPermission('movie_detail.access') && onMovieClick(mv.id)}>
              <Poster posterUrl={mv.poster_url} genre={mv.genre} letter={nameOf(mv)[0] || '?'} size="md" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--txt)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {mv.is_filtered && <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--danger)', marginLeft: 6 }}>מסונן</span>}
                      {nameOf(mv)}
                    </div>
                    {(mv.genre || mv.distributor) && (
                      <div data-card-sub style={{ fontSize: 11, color: 'var(--faint)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {[mv.genre, mv.distributor].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                  <div style={{ fontWeight: 800, fontSize: 17, color: 'var(--accent)', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{formatNumber(mv.total_tickets_sold)}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, fontSize: 11, color: 'var(--faint)', flexWrap: 'wrap' }}>
                  <AgeChip label={mv.age_label} />
                  <DateText value={mv.first_screening_date} today={today} />
                  {mv.screenings_count > 0 && (
                    <span style={{ marginInlineStart: 'auto', whiteSpace: 'nowrap' }}>
                      {formatNumber(mv.screenings_count)} הקרנות
                      {hasPermission('movies.occupancy_bar') && <> · <Occ value={mv.avg_occupancy} /></>}
                    </span>
                  )}
                </div>
              </div>
              {hasAnyMovieAction && menuButton(mv)}
            </div>
          ))}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={pageRows}
          onRowClick={hasPermission('movie_detail.access') ? (row) => onMovieClick(row.id) : undefined}
        />
      ))}

      {pager}
    </div>
  );
}

export default MoviesPage;
